/**
 * #40 · Denuncia de robo de un rollo.
 *
 * Un rollo robado del depósito es el vector clásico de falsificación **con
 * etiquetas auténticas**: son etiquetas que el sistema emitió de verdad, en un
 * producto que la marca nunca fabricó. Por eso esta llamada:
 *
 *   · pasa el rollo a `stolen`, que es un estado sin salida — `ROLL_TRANSITIONS`
 *     no lo deja ir a ningún lado;
 *   · pasa todas sus unidades todavía no activadas a `STOLEN` en un solo
 *     UPDATE por rango (las ya activadas NO se tocan: un rollo denunciado meses
 *     después no puede desactivar producto legítimo que ya está en la calle);
 *   · hace que cada escaneo posterior de cualquiera de esas etiquetas genere
 *     una alerta **crítica** `stolen_roll`.
 *
 * De ahí los dos pasos y el relato obligatorio: `RollStolenIn.note` es
 * requerido en la API con la razón textual "la alerta crítica que esto genera
 * la va a leer un investigador que no estuvo acá". El formulario lo dice con
 * esas palabras en vez de poner "campo obligatorio".
 */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';

import { rollKeys } from '@/api/keys';
import type { Schemas } from '@/api/types';
import { useApi } from '@/api/useApi';
import { useI18n } from '@/i18n/useI18n';
import { Button } from '@/ui/Button';
import { ErrorState } from '@/ui/states';
import { Dialog } from '../products/ui/Dialog';
import { DetailList, DetailRow, TextAreaField, TextField } from '../products/ui/fields';
import { serialRange } from './custody';

type Roll = Schemas['RollOut'];
type TransitionOut = Schemas['TransitionOut'];

/** Mínimo del relato. Es el mismo que valida `RollStolenIn`. */
const MIN_NOTE = 3;

type Step = 'scope' | 'confirm' | 'done';

export interface RollStolenDialogProps {
  roll: Roll;
  onClose: () => void;
}

/**
 * Se monta sólo mientras la denuncia está en curso y se desmonta al cerrar,
 * por el mismo motivo que `RecallDialog`: desmontar tira el estado del
 * formulario y evita que el segundo paso quede pre-confirmado.
 */
export function RollStolenDialog({ roll, onClose }: RollStolenDialogProps): ReactNode {
  const { t, fmt } = useI18n();
  const api = useApi();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>('scope');
  const [note, setNote] = useState('');
  const [policeReport, setPoliceReport] = useState('');
  const [location, setLocation] = useState('');
  const [typedCode, setTypedCode] = useState('');
  const [touched, setTouched] = useState(false);

  const expectedCode = roll.roll_code ?? String(roll.id);

  const report = useMutation<TransitionOut, unknown>({
    mutationFn: () =>
      api.post<TransitionOut>(`/api/v1/rolls/${String(roll.id)}/stolen`, {
        note: note.trim(),
        police_report: policeReport.trim() === '' ? null : policeReport.trim(),
        location: location.trim() === '' ? null : location.trim(),
      }),
    onSuccess: () => {
      setStep('done');
      void queryClient.invalidateQueries({ queryKey: rollKeys.all });
    },
  });

  const noteError =
    touched && note.trim().length < MIN_NOTE ? t('labels.stolen.note.tooShort') : undefined;
  const codeMatches = typedCode.trim() === expectedCode;

  return (
    <Dialog
      open
      onClose={onClose}
      tone={step === 'done' ? 'neutral' : 'critical'}
      title={step === 'done' ? t('labels.stolen.done.title') : t('labels.stolen.title')}
      description={
        step === 'done'
          ? undefined
          : step === 'scope'
            ? t('labels.stolen.step1.description')
            : t('labels.stolen.step2.description')
      }
      footer={
        step === 'done' ? (
          <Button variant="primary" onClick={onClose}>
            {t('batches.recall.done.close')}
          </Button>
        ) : step === 'scope' ? (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('form.cancel')}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                setTouched(true);
                if (note.trim().length >= MIN_NOTE) setStep('confirm');
              }}
            >
              {t('batches.recall.continue')}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setStep('scope');
              }}
              disabled={report.isPending}
            >
              {t('batches.recall.back')}
            </Button>
            <Button
              variant="critical"
              disabled={!codeMatches || report.isPending}
              onClick={() => {
                report.mutate();
              }}
            >
              {report.isPending ? t('labels.stolen.reporting') : t('labels.stolen.confirm')}
            </Button>
          </>
        )
      }
    >
      {step === 'scope' && (
        <div className="space-y-4">
          <div className="rounded-(--radius-card) border border-border bg-surface-sunken p-4">
            <DetailList>
              <DetailRow
                label={t('labels.rolls.column.code')}
                value={<code className="font-mono font-semibold">{expectedCode}</code>}
              />
              <DetailRow
                label={t('labels.rolls.column.serials')}
                value={
                  <code className="font-mono text-xs">
                    {serialRange(roll.serial_from, roll.serial_to, (value) => fmt.number(value)) ??
                  '—'}
                  </code>
                }
              />
              <DetailRow
                label={t('labels.stolen.scope.units')}
                value={
                  <span className="text-base font-semibold text-critical">
                    {fmt.number(roll.unit_count)}
                  </span>
                }
              />
              <DetailRow label={t('labels.rolls.column.supplier')} value={roll.supplier ?? '—'} />
            </DetailList>
          </div>

          <ul className="list-disc space-y-1 pl-5 text-sm text-ink">
            <li>{t('labels.stolen.effects.units')}</li>
            <li>{t('labels.stolen.effects.alerts')}</li>
            <li>{t('labels.stolen.effects.activated')}</li>
            <li className="font-medium text-critical">{t('labels.stolen.effects.irreversible')}</li>
          </ul>

          <TextAreaField
            label={t('labels.stolen.note')}
            hint={t('labels.stolen.note.hint')}
            value={note}
            onChange={setNote}
            error={noteError}
            required
            rows={3}
            maxLength={500}
          />

          <TextField
            label={t('labels.stolen.policeReport')}
            hint={t('labels.stolen.policeReport.hint')}
            value={policeReport}
            onChange={setPoliceReport}
            maxLength={120}
            mono
          />

          <TextField
            label={t('labels.stolen.location')}
            value={location}
            onChange={setLocation}
            maxLength={200}
          />
        </div>
      )}

      {step === 'confirm' && (
        <div className="space-y-4">
          <p className="text-sm text-ink">
            {t('labels.stolen.confirm.summary', {
              code: expectedCode,
              units: fmt.number(roll.unit_count),
            })}
          </p>
          <TextField
            label={t('labels.stolen.typeCode', { code: expectedCode })}
            hint={t('batches.recall.typeLot.hint')}
            value={typedCode}
            onChange={setTypedCode}
            error={
              typedCode.length > 0 && !codeMatches ? t('batches.recall.typeLot.mismatch') : undefined
            }
            required
            mono
            disabled={report.isPending}
          />
          {Boolean(report.error) && <ErrorState error={report.error} />}
        </div>
      )}

      {step === 'done' && report.data !== undefined && (
        <div className="space-y-3">
          <p className="text-sm text-ink">
            {t('labels.stolen.done.detail', {
              code: expectedCode,
              units: fmt.number(report.data.units_affected),
            })}
          </p>
          <p className="text-sm text-ink-muted">{t('labels.stolen.done.next')}</p>
        </div>
      )}
    </Dialog>
  );
}
